import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, isNull } from "lodash";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";

import {
  Button,
  Card,
  Col,
  Form,
  HelperText,
  InputGroup,
  Row,
  Spinner,
} from "@deere/ux.uxframe-react";
import { ReactComponent as ChevronRight } from "@deere/ux.brand-foundations/icons/chevron_right.svg";
import { ReactComponent as ChevronDown } from "@deere/ux.brand-foundations/icons/chevron_down.svg";
import { ReactComponent as ChevronLeft } from "@deere/ux.brand-foundations/icons/chevron_left.svg";

import {
  getMachineDetails,
  getMachines,
  machineDetails,
  machineListError,
  machineListLoading,
  resetMachineDetails,
  resetMachineError,
  setSearchedPin,
} from "./machineSearchSlice";

import { getClaims } from "../claim-search/claimSearchSlice";
import {
  getProductComponents,
  getProductOptions,
  productOptions,
} from "../product-config/productConfigSlice";
import { getPipInfo } from "../pip-search/pipSearchSlice";
import { getWarrantyInfo } from "../warranty-search/warrantySearchSlice";
import {
  customerHistory,
  getCustomerHistoryForMachine,
} from "../customer-search/customerHistorySlice";
import {
  isHighRiskDealer,
  relatedAccounts,
} from "../dealer-search/DealerSlice";
import PipList from "../pip-search/PipList";
import ClaimList from "../claim-search/ClaimList";
import OptionList from "../product-config/OptionList";
import ComponentList from "../product-config/ComponentList";
import WarrantyList from "../warranty-search/WarrantyList";
import CustomerHistory from "../customer-search/CustomerHistory";
import ExportMachineDetails from "../export-to-excel/ExportMachineDetails";
import { languageResponseData } from "../Translation/translationSerachSlice";

const HTML_ESCAPE_MAP = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;",
};

const escapeHtml = (str) =>
  String(str).replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch]);

const MachineDetail = () => {
  const translationsResponse = useSelector(languageResponseData);
  const translate = (key, defaultText) => {
    return translationsResponse[key] || defaultText;
  };
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  let { serialNumber } = useParams();
  const errorMessage = useSelector(machineListError);
  const prodOptions = useSelector(productOptions);
  const [serialNumSearchText, setSerialNumSearchText] = useState(
    serialNumber || ""
  );
  const [collapsibleSections, setCollapsibleSections] = useState({
    machineDetailsVisible: true,
    warrantyCoverageVisible: true,
    compSerialNumVisible: true,
    optionCodeVisible: true,
    customerHistoryVisible: true,
    claimHistoryVisible: true,
    pipHistoryVisible: true,
  });

  const machineSearchType = location.state?.machineSearchType;
  const dealerReportRow = location.state?.dealerReportRow;

  let machine = useSelector(machineDetails);
  const machineSearchLoading = useSelector(machineListLoading);
  const isDealerRestricted = useSelector(isHighRiskDealer);
  const allowedDealerList = useSelector(relatedAccounts);
  /*Get customer data from customer history where customer data is not present in EDL*/
  /*
   * Use the customer history data to show the Customer data on top of Machine details page.
   * This is to handle the situation where the JDWS API is not returning the ckcEntityID.
   * */
  const customerHistoryList = useSelector(customerHistory);
  let paramSerialNumber = serialNumber;

  /*if (serialNumber?.length === 17 && Array.isArray(machines)) {
        machine = machines.find(item => item.pinNumber17 === serialNumber);
    } else if (serialNumber?.length === 13 && Array.isArray(machines)) {
        machine = machines.find(item => item.pinNumber13 === serialNumber);
    } else if (serialNumber && typeof machines === 'object') {
        machine = machines;
    } else {
        machine = machineDetail;
    }*/

  if (
    serialNumber?.length === 17 &&
    machine !== null &&
    machine?.pinNumber17 !== serialNumber &&
    serialNumber?.length === 13 &&
    machine !== null &&
    machine?.pinNumber13 !== serialNumber &&
    serialNumber?.length > 10 &&
    machine !== null &&
    machine?.ntvPin !== serialNumber
  ) {
    machine = null;
  }

  const printMachineDetails = (divId, searchSerialNumber) => {
    const searchSerialNumberLabel = `<div><b>PIN/Serial Number: ${escapeHtml(searchSerialNumber)}</b></div><br>`;
    const printContents =
      searchSerialNumberLabel + document.getElementById(divId).innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  /*Fetch Customer Data*/
  /*
   * Cases when useEffect will fetch machine details:
   * 1. When the machine details are not present in the redux store.
   * 2. When the serial number in the URL is different from the serial number in the redux store.
   *
   * */

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top of the page
    if (machine !== null) {
      if (machine?.pinNumber17) {
        serialNumber = machine?.pinNumber17;
      } else if (machine?.ntvPin) {
        serialNumber = machine?.ntvPin;
      } else {
        serialNumber = machine?.pinNumber13;
      }

      /*Setting default serial Number on Display*/
      setSerialNumSearchText(serialNumber);
      let claimSerialNumber;
      if (machine?.pinNumber13) claimSerialNumber = machine?.pinNumber13;
      else claimSerialNumber = serialNumber;

      const claimSearchObj = {
        searchType: "SerialNumber",
        pin: claimSerialNumber,
      };
      const optionSearchObj = {
        serialNumber: serialNumber,
        configType: "Shipped Configuration",
      };
      const pipSearchObj = {
        pin: serialNumber,
      };
      const warrantySearchObj = {
        pin: serialNumber,
        prodId: machine?.prodId,
      };

      dispatch(getClaims(claimSearchObj));
      dispatch(getProductComponents(serialNumber));
      dispatch(getProductOptions(optionSearchObj));
      dispatch(getPipInfo(pipSearchObj));
      dispatch(getWarrantyInfo(warrantySearchObj));
      dispatch(getCustomerHistoryForMachine({ pin: serialNumber }));
    } else {
      dispatch(setSearchedPin(paramSerialNumber));
      dispatch(getMachineDetails(paramSerialNumber));
    }

    return () => {
      if (errorMessage) {
        dispatch(resetMachineDetails());
        dispatch(resetMachineError());
      }
      if (machine) {
        dispatch(resetMachineDetails());
      }
    };
    /*Search PIPs for the machine.*/
  }, [machine, serialNumber]);

  const searchSerialNumber = (event) => {
    event.preventDefault();

    /*Set searched pin to redux state to preserve it across components.*/
    dispatch(setSearchedPin(serialNumSearchText));

    if (serialNumSearchText?.length === 6) {
      const searchObj = {
        searchedPin: serialNumSearchText,
        searchType: "partialPin",
      };
      dispatch(getMachines(searchObj));
      history.push("/machine-search");
    } else if (
      serialNumSearchText?.length >= 13 &&
      serialNumSearchText?.length <= 17
    ) {
      dispatch(resetMachineDetails());
      history.push({
        pathname: `/machine-details/pin/${serialNumSearchText}`,
        state: {
          pinNumber: serialNumSearchText,
          machineSearchType: "machines",
          searchLength: (serialNumSearchText ?? "").length,
        },
      });
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    if (dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", options);
    } else return "";
  };

  const onCollapseCard = (cardName) => {
    setCollapsibleSections({
      ...collapsibleSections,
      [cardName]: !collapsibleSections[cardName],
    });
  };

  const submitDisabled = () => {
    return !(
      serialNumSearchText?.length === 6 ||
      (serialNumSearchText?.length >= 13 && serialNumSearchText?.length <= 17)
    );
  };
  const handleSearchSerialNumChange = (e) => {
    setSerialNumSearchText(e.target.value.trim().toUpperCase());
  };

  const backButtonStyle = {
    minWidth: "92px",
    height: "38px",
    backgroundColor: "#efefef",
    borderColor: "#8a8a8a",
    color: "#3a3a3a",
    borderRadius: "4px",
    fontWeight: 600,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  };

  const onBackClick = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push("/machine-search");
    }
  };

  const getCurrentCustomer = () => {
    if (Array.isArray(customerHistoryList)) {
      return customerHistoryList.find(
        (customer) => customer.activeCustomer === true
      );
    }
  };

  return (
    <div
      style={{ fontSize: ".8rem" }}
      id={"machine-details"} /*ref={machineDetailsRef}*/
    >
      {(machineSearchType === "customer" || machineSearchType === "machines") &&
        !errorMessage && (
          <div>
            <div
              className="mb-3 mt-3"
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                minWidth: 0,
                gap: "12px",
              }}
            >
              <InputGroup className="col-8 mb-0">
                <Form
                  onSubmit={searchSerialNumber}
                  style={{ display: "inline-flex" }}
                >
                  <Form.Control
                    aria-label="Machine serial number"
                    style={{ width: "auto", minWidth: "100px" }}
                    maxLength={17}
                    value={serialNumSearchText}
                    onChange={handleSearchSerialNumChange}
                  />
                  <InputGroup.Append>
                    <Button
                      variant="primary"
                      disabled={submitDisabled()}
                      onClick={searchSerialNumber}
                    >
                      {translate("search", "Search")}
                    </Button>
                  </InputGroup.Append>
                </Form>
              </InputGroup>
              <Button
                variant="secondary"
                style={{ ...backButtonStyle, marginLeft: "auto", flexShrink: 0 }}
                onClick={onBackClick}
              >
                <ChevronLeft />
                Back
              </Button>
            </div>
          </div>
        )}

      {(machineSearchType === "customer" || machineSearchType === "machines") &&
        errorMessage && (
          <Card>
            <Card.Body>
              <HelperText
                helperTextId="machineSearchHelper"
                helperText={translate(
                  "message1",
                  "Please enter a partial or complete PIN."
                )}
                sx={{ fontSize: ".8rem" }}
              >
                <div
                  className="mb-4 mt-3"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    minWidth: 0,
                    gap: "12px",
                  }}
                >
                  <InputGroup className="col-8 mb-0">
                    <Form
                      onSubmit={searchSerialNumber}
                      style={{ display: "inline-flex" }}
                    >
                      <Form.Control
                        aria-label="Machine serial number"
                        style={{ width: "auto", minWidth: "100px" }}
                        maxLength={17}
                        value={serialNumSearchText}
                        onChange={handleSearchSerialNumChange}
                      />
                      <InputGroup.Append>
                        <Button
                          variant="primary"
                          disabled={submitDisabled()}
                          onClick={searchSerialNumber}
                        >
                          {translate("search", "Search")}
                        </Button>
                      </InputGroup.Append>
                    </Form>
                  </InputGroup>
                  <Button
                    variant="secondary"
                    style={{ ...backButtonStyle, marginLeft: "auto", flexShrink: 0 }}
                    onClick={onBackClick}
                  >
                    <ChevronLeft />
                    Back
                  </Button>
                </div>
              </HelperText>
              {errorMessage ? (
                <span
                  style={{
                    color: "red",
                    marginTop: 0.5,
                    fontSize: 20,
                    marginLeft: 300,
                  }}
                >
                  {translate(
                    "message16",
                    "No machines found for this criteria."
                  )}
                </span>
              ) : (
                <></>
              )}
            </Card.Body>
          </Card>
        )}
      {!errorMessage && (
        <div>
          <Row
            style={{
              marginBottom: "1rem",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              variant="secondary"
              onClick={() =>
                printMachineDetails("machine-details-data", serialNumber)
              }
            >
              {translate("print_to_pdf", "Print to Pdf")}
            </Button>{"|" === "|" ? " " : ""}
            <span aria-hidden="true" style={{ marginRight: "16px" }} />
            <ExportMachineDetails machineDetails={machine} />
          </Row>
          <div id="machine-details-data">
            {/*<ReactToPrint as={Button}
                                            trigger={() => <button className="print-button">Print Machine Details</button>}
                                            content={() => machineDetailsRef.current}
                              />*/}
            <Card>
              <Card.Header
                onClick={() => onCollapseCard("machineDetailsVisible")}
              >
                {collapsibleSections.machineDetailsVisible ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <strong>
                  {translate("machine_details", "Machine Details")}
                </strong>
              </Card.Header>
              {collapsibleSections.machineDetailsVisible && (
                <Card.Body>
                  {machineSearchLoading ? (
                    <div className="d-flex w-100 justify-content-center align-items-center">
                      <Spinner
                        accessibilityLabel="Loading"
                        className="uxf-spinner-border-lg"
                        animation="border"
                      />
                    </div>
                  ) : (
                    <ul className="list-unstyled uxf-line-separated mb-0">
                      <li>
                        <Row>
                          <Col>
                            <CustomerData
                              machine={machine}
                              isDealerRestricted={isDealerRestricted}
                              allowedDealerList={allowedDealerList}
                              customerData={getCurrentCustomer()}
                            />
                          </Col>
                          <Col>
                            {machine && (
                              <dl className="uxf-dl uxf-dl-horizontal mb-md-0">
                                <dt>
                                  {translate(
                                    "customer_number",
                                    "Customer Number"
                                  )}
                                </dt>
                                {/*<dd>{machine.entityId && machine.entityId !== 0 ? machine.entityId : customerHistoryList[0]?.customer?.entityId}</dd>*/}
                                <dd>
                                  {machine.entityId && machine.entityId !== 0
                                    ? machine.entityId
                                    : getCurrentCustomer()?.customer?.entityId}
                                </dd>
                                <dt>
                                  {translate("purchase_type", "Purchase Type")}
                                </dt>
                                <dd>{machine.purchaseTypeName}</dd>
                                <dt>
                                  {translate("end_use_code", "End Use Code")}
                                </dt>
                                <dd>{machine.endUseDescription}</dd>
                                <dt>
                                  {translate("delivery_date", "Delivery Date")}
                                </dt>
                                {/* <dd>{machine?.initialDeliveryDate ? formatDate(machine.initialDeliveryDate) : formatDate(customerHistoryList[customerHistoryList.length - 1]?.deliveryReceiptDate)}</dd>*/}
                                <dd>
                                  {formatDate(machine?.deliveryReceiptDate)}
                                </dd>
                                <dt>
                                  {translate(
                                    "settlement_date",
                                    "Settlement Date"
                                  )}
                                </dt>
                                <dd>{formatDate(machine.settlementDate)}</dd>
                                <dt>
                                  {translate("invoive_date", "Invoice Date")}
                                </dt>
                                <dd>{formatDate(machine.dealerInvoiceDate)}</dd>
                                <dt>
                                  {translate(
                                    "registration_date",
                                    "Registration Date"
                                  )}
                                </dt>
                                <dd>{formatDate(machine.registrationDate)}</dd>
                                <dt>
                                  {translate(
                                    "invoice_number",
                                    "Invoice Number"
                                  )}
                                </dt>
                                <dd>
                                  {machine?.dealerInvoiceNumber ??
                                    dealerReportRow?.dlrInvNum ??
                                    ""}
                                </dd>
                                <dt>
                                  {translate(
                                    "dealer_contact",
                                    "Dealer Contact"
                                  )}
                                </dt>
                                <dd>
                                  {machine?.businessPhone ??
                                    dealerReportRow?.businessPhone ??
                                    ""}
                                </dd>
                              </dl>
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            {machine && (
                              <dl className="uxf-dl uxf-dl-horizontal mb-md-0">
                                <dt>
                                  {translate(
                                    "selling_dealer",
                                    "Selling Dealer"
                                  )}
                                </dt>
                                <dd>
                                  {machine.soldByDealerNum ||
                                  machine.sellingDealerName ||
                                  machine.sellingDealerCity ||
                                  machine.sellingDealerState
                                    ? `${
                                        machine.soldByDealerNum
                                          ? machine.soldByDealerNum + "-"
                                          : ""
                                      }${machine.sellingDealerName || ""}${
                                        machine.sellingDealerCity
                                          ? "," + machine.sellingDealerCity
                                          : ""
                                      }${
                                        machine.sellingDealerState
                                          ? "," + machine.sellingDealerState
                                          : ""
                                      }`
                                    : ""}
                                </dd>
                                <dt>
                                  {translate(
                                    "responsible_dealer",
                                    "Responsible Dealer"
                                  )}
                                </dt>
                                <dd>
                                  {machine.serviceDealerNum ||
                                  machine.serviceDealerName ||
                                  machine.serviceDealerCity ||
                                  machine.serviceDealerState
                                    ? `${
                                        machine.serviceDealerNum
                                          ? machine.serviceDealerNum + "-"
                                          : ""
                                      }${machine.serviceDealerName || ""}${
                                        machine.serviceDealerCity
                                          ? "," + machine.serviceDealerCity
                                          : ""
                                      }${
                                        machine.serviceDealerState
                                          ? "," + machine.serviceDealerState
                                          : ""
                                      }`
                                    : ""}
                                </dd>
                              </dl>
                            )}
                          </Col>
                          <Col>
                            <div aria-hidden="true" />
                          </Col>
                        </Row>
                      </li>
                      <li>
                        <Row>
                          <Col>
                            <dl>
                              <dt>{translate("model", "Model")}</dt>
                              <dd>{machine?.modelName}</dd>
                            </dl>
                          </Col>
                          <Col>
                            <dl>
                              <dt>{translate("model_year", "Model Year")}</dt>
                              <dd>{machine?.modelYear}</dd>
                            </dl>
                          </Col>
                          <Col>
                            <dl>
                              <dt>{translate("description", "Description")}</dt>
                              <dd>{machine?.modelDescription}</dd>
                            </dl>
                          </Col>
                          <Col>
                            <dl>
                              <dt>{translate("branch", "Branch")}</dt>
                              <dd>{machine?.mru ?? dealerReportRow?.mru ?? ""}</dd>
                            </dl>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            {/*  <dl>
                                                                      <dt>Latest Delivery Date</dt>
                                                                      <dd></dd>
                                                                      {formatDate(machine?.deliveryReceiptDate)}</dl>*/}
                            <dl>
                              <dt>
                                {translate(
                                  "machine_build_date",
                                  "Machine Build Date"
                                )}
                              </dt>
                              {/*<dd>{formatDate(prodOptions?.productDetails?.mfgDate)}</dd>*/}
                              <dd>
                                {machine?.factoryBuildDate != null
                                  ? formatDate(machine?.factoryBuildDate)
                                  : formatDate(
                                      prodOptions?.productDetails?.mfgDate
                                    )}
                              </dd>
                            </dl>
                          </Col>
                          <Col>
                            <dl>
                              <dt>{translate("decal_model", "Decal Model")}</dt>
                              <dd>
                                {machine?.decalModel ??
                                  dealerReportRow?.ctgryNm ??
                                  ""}
                              </dd>
                            </dl>
                          </Col>
                          <Col>
                            <dl>
                              <dt>{translate("tcsm", "TCSM")}</dt>
                              <dd>
                                {machine?.servTerritoryDesc ??
                                  dealerReportRow?.servTerritoryDsc ??
                                  ""}
                              </dd>
                            </dl>
                          </Col>
                          <Col>
                            <dl>
                              <dt>{translate("service_territory", "Service Territory")}</dt>
                              <dd>
                                {machine?.serviceTerritory ??
                                  [
                                    dealerReportRow?.serviceDivision,
                                    dealerReportRow?.serviceTerritory,
                                  ]
                                    .filter(Boolean)
                                    .join("")}
                              </dd>
                            </dl>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            
                            <dl>
                              <dt>
                                {translate("machine_status", "Machine Status")}
                              </dt>
                              <dd>
                                {machine?.newUsedIndicator?.length > 1
                                  ? machine?.newUsedIndicator
                                  : machine?.newUsedIndicator === "U"
                                  ? "Used Machine"
                                  : "New Machine"}
                              </dd>
                            </dl>
                          </Col>
                          <Col aria-hidden="true" />
                          <Col aria-hidden="true" />
                          <Col>
                            <dl>
                              <dt>{translate("manufacturing_factory", "Manufacturing Factory")}</dt>
                              <dd>{machine?.manufacturingFactory}</dd>
                            </dl>
                            {!!machine?.lastReportedMeasurement && (
                              <dl>
                                <dt>
                                  {translate(
                                    "last_reported_registration",
                                    "Last Reported Registration Measurement"
                                  )}
                                </dt>
                                <dd>{machine?.lastReportedMeasurement}</dd>
                              </dl>
                            )}

                            {/*<dl>
                                                                      <dt>Last Reported Claim Measurement</dt>
                                                                      <dd></dd>
                                                                  </dl>
                                                                  <dl>
                                                                      <dt>JDLink Expiration Date</dt>
                                                                      <dd></dd>
                                                                  </dl>
                                                                  <dl>
                                                                      <dt>Approximate Cumulative Rental Days</dt>
                                                                      <dd></dd>
                                                                  </dl>*/}
                          </Col>
                        </Row>
                      </li>
                    </ul>
                  )}
                </Card.Body>
              )}
            </Card>

            <Card>
              <Card.Header
                onClick={() => onCollapseCard("warrantyCoverageVisible")}
              >
                {collapsibleSections.warrantyCoverageVisible ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <strong>
                  {translate("warranty_coverage", "Warranty Coverage")}
                </strong>
              </Card.Header>
              {collapsibleSections.warrantyCoverageVisible && (
                <Card.Body>
                  <WarrantyList />
                </Card.Body>
              )}
            </Card>

            <Card>
              <Card.Header
                onClick={() => onCollapseCard("compSerialNumVisible")}
              >
                {collapsibleSections.compSerialNumVisible ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <strong>
                  {translate("component_serial_no", "Component Serial Numbers")}
                </strong>
              </Card.Header>
              {collapsibleSections.compSerialNumVisible && (
                <Card.Body>
                  <ComponentList />
                </Card.Body>
              )}
            </Card>

            <Card>
              <Card.Header onClick={() => onCollapseCard("optionCodeVisible")}>
                {collapsibleSections.optionCodeVisible ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <strong>
                  {translate("base_option_code", "Order Base/Option Codes")}
                </strong>
              </Card.Header>
              {collapsibleSections.optionCodeVisible && (
                <Card.Body>
                  <OptionList />
                </Card.Body>
              )}
            </Card>

            <Card>
              <Card.Header
                onClick={() => onCollapseCard("customerHistoryVisible")}
              >
                {collapsibleSections.customerHistoryVisible ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <strong>
                  {translate("customer_history", "Customer History")}
                </strong>
              </Card.Header>
              {collapsibleSections.customerHistoryVisible && (
                <Card.Body>
                  {!isDealerRestricted ||
                  allowedDealerList?.includes(machine?.serviceDealerNum) ? (
                    <CustomerHistory restrictedDetails="false" />
                  ) : (
                    <CustomerHistory restrictedDetails="true" />
                  )}
                </Card.Body>
              )}
            </Card>

            <Card>
              <Card.Header
                onClick={() => onCollapseCard("claimHistoryVisible")}
              >
                {collapsibleSections.claimHistoryVisible ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <strong>{translate("claim_history", "Claim History")}</strong>
              </Card.Header>
              {collapsibleSections.claimHistoryVisible && (
                <Card.Body>
                  <ClaimList searchFrom="MachineDetails" />
                </Card.Body>
              )}
            </Card>

            <Card>
              <Card.Header onClick={() => onCollapseCard("pipHistoryVisible")}>
                {collapsibleSections.pipHistoryVisible ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
                <strong>{translate("pip_history", "PIP History")}</strong>
              </Card.Header>
              {collapsibleSections.pipHistoryVisible && (
                <Card.Body>
                  <PipList />
                </Card.Body>
              )}
            </Card>
          </div>
          <Row
            style={{
              marginBottom: ".5rem",
              marginTop: "1rem",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              variant="secondary"
              onClick={() =>
                printMachineDetails("machine-details-data", serialNumber)
              }
            >
              {translate("print_to_pdf", "Print to Pdf")}
            </Button>{" "}
            <span aria-hidden="true" style={{ marginRight: "16px" }} />
            <ExportMachineDetails machineDetails={machine} />
          </Row>
        </div>
      )}
    </div>
  );
};

const CustomerData = ({
  machine,
  isDealerRestricted,
  allowedDealerList,
  customerData,
}) => {
  const translationsResponse = useSelector(languageResponseData);
  const translate = (key, defaultText) => {
    return translationsResponse[key] || defaultText;
  };

  let name = "";
  let addr1, addr2, homePhone, mobilePhone, workPhone;
  let customer;
  const restrictCustomerInformation =
    isDealerRestricted &&
    !allowedDealerList?.includes(machine?.serviceDealerNum);
  if (machine?.entityId && machine?.entityId !== 0) {
    if (machine?.businessName1 && machine?.businessName1.trim() !== "") {
      name = machine?.businessName1;
    } else if (machine?.businessName2 && machine?.businessName2.trim() !== "") {
      name = machine?.businessName2;
    } else if (
      machine?.firstName &&
      machine?.lastName &&
      machine?.firstName.trim() !== "" &&
      machine?.lastName.trim() !== ""
    ) {
      const middleName =
        machine?.middleName && machine?.middleName.trim() !== ""
          ? machine?.middleName
          : "";
      name = machine?.firstName + " " + middleName + " " + machine?.lastName;
    }
    if (!restrictCustomerInformation) {
      addr1 = machine?.streetAddr1.trim() !== "" ? machine?.streetAddr1 : "";
      const city =
        machine?.city && machine?.city.trim() !== "" ? machine?.city : "";
      const stateProvince =
        machine?.stateProvince && machine?.stateProvince.trim() !== ""
          ? machine?.stateProvince
          : "";
      const iso2CountryCode =
        machine?.iso2CountryCode && machine?.iso2CountryCode.trim() !== ""
          ? machine?.iso2CountryCode
          : "";
      const zipCode =
        machine?.zipCode && machine?.zipCode.trim() !== ""
          ? machine?.zipCode
          : "";
      addr2 =
        city + " " + stateProvince + " " + iso2CountryCode + " " + zipCode;
      homePhone =
        machine?.homePhoneCountryCode +
        machine?.homePhoneAreaCode +
        machine?.homePhoneNum;
      mobilePhone =
        machine?.mobilePhoneCountryCode +
        machine?.mobilePhoneAreaCode +
        machine?.mobilePhoneNum;
      workPhone =
        machine?.workPhoneCountryCode +
        machine?.workPhoneAreaCode +
        machine?.workPhoneNum;
    }

    customer = {
      entityId: machine?.entityId,
      businessName1:
        machine?.businessName1 ||
        machine?.businessName2 ||
        machine?.firstName +
          " " +
          machine?.middleName +
          " " +
          machine?.lastName,

      addresses: [
        {
          street: machine?.streetAddr1,
          street2: machine?.streetAddr2,
          city: machine?.city,
          state: machine?.stateProvince,
          country: machine?.iso2CountryCode,
        },
      ],
      homePhone: {
        countryCode: machine?.homePhoneCountryCode,
        areaCode: machine?.homePhoneAreaCode,
        phoneNumber: machine?.homePhoneNum,
      },
      mobilePhone: {
        countryCode: machine?.mobilePhoneCountryCode,
        areaCode: machine?.mobilePhoneAreaCode,
        phoneNumber: machine?.mobilePhoneNum,
      },
      workPhone: {
        countryCode: machine?.workPhoneCountryCode,
        areaCode: machine?.workPhoneAreaCode,
        phoneNumber: machine?.workPhoneNum,
        workPhoneNumExtn: machine?.workPhoneNumExtn,
      },
    };
  } else {
    /*Create customer Data from the Customer history list*/
    /*This will be used when the Machine Data does not have a customer Name in it.*/
    const activeCustomer = customerData?.customer;

    const physicalAddress = activeCustomer?.addresses[0];
    const homePhoneActive = activeCustomer?.homePhone;
    const workPhoneActive = activeCustomer?.workPhone;
    const mobilePhoneActive = activeCustomer?.mobilePhone;

    if (activeCustomer?.customerType === "INDIVIDUAL") {
      name = activeCustomer?.firstName + " " + activeCustomer?.lastName;
    } else if (activeCustomer?.customerType === "BUSINESS") {
      name = activeCustomer?.businessName1 || activeCustomer?.businessName2;
    } else {
      name = "";
    }

    if (!restrictCustomerInformation) {
      addr1 = physicalAddress?.street;
      const city =
        physicalAddress?.city && physicalAddress?.city.trim() !== ""
          ? physicalAddress.city
          : "";
      const stateProvince =
        physicalAddress?.state && physicalAddress?.state.trim() !== ""
          ? physicalAddress?.state
          : "";
      const iso2CountryCode =
        physicalAddress?.country && physicalAddress?.country.trim() !== ""
          ? physicalAddress?.country
          : "";
      const zipCode =
        physicalAddress?.postalCode && physicalAddress?.postalCode.trim() !== ""
          ? physicalAddress?.postalCode
          : "";
      addr2 =
        city + " " + stateProvince + " " + iso2CountryCode + " " + zipCode;
      homePhone =
        homePhoneActive?.countryCode +
        homePhoneActive?.areaCode +
        homePhoneActive?.phoneNumber;
      mobilePhone =
        mobilePhoneActive?.countryCode +
        mobilePhoneActive?.areaCode +
        mobilePhoneActive?.phoneNumber;
      workPhone =
        workPhoneActive?.countryCode +
        workPhoneActive?.areaCode +
        workPhoneActive?.phoneNumber;
    }

    customer = {
      entityId: activeCustomer?.entityId,
      businessName1: activeCustomer?.businessName1,
      businessName2: activeCustomer?.businessName2,
      firstName: activeCustomer?.firstName,
      middleName: activeCustomer?.middleName,
      lastName: activeCustomer?.lastName,

      addresses: [
        {
          street: physicalAddress?.street,
          street2: physicalAddress?.street2,
          city: physicalAddress?.city,
          state: physicalAddress?.state,
          country: physicalAddress?.country,
        },
      ],
      homePhone: {
        countryCode: homePhoneActive?.countryCode,
        areaCode: homePhoneActive?.areaCode,
        phoneNumber: homePhoneActive?.phoneNumber,
      },
      mobilePhone: {
        countryCode: mobilePhoneActive?.countryCode,
        areaCode: mobilePhoneActive?.areaCode,
        phoneNumber: mobilePhoneActive?.phoneNumber,
      },
      workPhone: {
        countryCode: workPhoneActive?.countryCode,
        areaCode: workPhoneActive?.areaCode,
        phoneNumber: workPhoneActive?.phoneNumber,
        workPhoneNumExtn: workPhoneActive?.extension,
      },
    };
  }
  return (
    <>
      <dl className="uxf-dl mr-2">
        <dt>{translate("customer", "Customer")}</dt>
        {customer &&
          (restrictCustomerInformation ? (
            <dd>{name}</dd>
          ) : (
            <dd>
              <Link
                to={{
                  pathname: `/machine-search/customer/${customer?.entityId}/machines`,
                  state: { customer },
                }}
              >
                {name}
              </Link>
            </dd>
          ))}
        <dd>{addr1}</dd>
        <dd>{addr2}</dd>
      </dl>
      <dl className="uxf-dl uxf-dl-horizontal mb-md-0">
        <dt>{translate("home", "Home:")}</dt>
        <dd>{!isNull(homePhone) && !isEmpty(homePhone) ? homePhone : ""}</dd>
        <dt>{translate("cell", "Cell:")}</dt>
        <dd>
          {!isNull(mobilePhone) && !isEmpty(mobilePhone) ? mobilePhone : ""}
        </dd>
        <dt>{translate("work", "Work:")}</dt>
        <dd>{!isNull(workPhone) && !isEmpty(workPhone) ? workPhone : ""}</dd>
      </dl>
    </>
  );
};

export default MachineDetail;
